import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useAutoplayABWizardStore } from '@/store/autoplay-ab-test';
import { useCreateAutoplayTest, useUserAutoplayTests } from '@/hooks/useAutoplayTest';
import { useAuth } from '@/hooks/useAuth';
import { autoplayTestService } from '@/services/autoplayTestService';
import {
  AutoplayABWizardStepper,
  AutoplayABWizardFooter,
  AutoplayABVariationsStep,
  AutoplayABOrderStep,
  AutoplayABTrafficStep,
  ActiveTestPanel
} from '../wizard';

const stepVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? 30 : -30,
    opacity: 0
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -30 : 30,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};

interface AutoplayTestTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const AutoplayTestTab: React.FC<AutoplayTestTabProps> = ({ features, setFeature }) => {
  useLanguage();
  const { currentStep, steps, data, setCurrentStep, nextStep, prevStep, completeStep, setEndDate, setDurationMode, reset } = useAutoplayABWizardStore();
  const [direction, setDirection] = React.useState(0);
  const [showActivePanel, setShowActivePanel] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { user } = useAuth();
  const { mutateAsync: createTest } = useCreateAutoplayTest();
  const { data: userAutoplayTests } = useUserAutoplayTests(user?.id);
  const videoId = features.videoId;

  const abTestConfig = features.autoplayOverlay?.abTestConfig || {
    enabled: false,
    variations: [],
    status: 'draft' as const
  };

  const safeVariations = Array.isArray(abTestConfig.variations) ? abTestConfig.variations : [];

  // Mostrar painel ativo se o teste já estiver ativo
  useEffect(() => {
    if (abTestConfig.status === 'active') {
      setShowActivePanel(true);
    }
  }, [abTestConfig.status]);

  // Carrega o teste de autoplay do backend para o vídeo atual — espelha o
  // padrão do HeadlineOverlayConfig (popular abTestConfig quando o usuário
  // abre /personalizations num vídeo que já tem teste rodando).
  useEffect(() => {
    if (!userAutoplayTests || !videoId) return;

    const currentVideoTest = userAutoplayTests.find(
      (test: any) => test.originalVideoId === videoId
    );

    if (currentVideoTest) {
      const testId = currentVideoTest.id || currentVideoTest.id_autoplay_test;
      if (abTestConfig.testId !== testId) {
        updateABTestConfig({
          enabled: true,
          status: 'active',
          testId,
          variations: currentVideoTest.variations || [],
          trafficDistribution: currentVideoTest.trafficDistribution,
          startDate: currentVideoTest.startDate || currentVideoTest.createdAt || new Date().toISOString(),
          endDate: currentVideoTest.endDate,
        });
      }
    }
  }, [userAutoplayTests, videoId]);

  // Verificar expiração: quando o backend remove o teste (lazy expiry por end_date),
  // ele deixa de aparecer na listagem — o frontend detecta a ausência e finaliza localmente
  useEffect(() => {
    if (!abTestConfig.testId || abTestConfig.status !== 'active' || !abTestConfig.endDate) return;
    if (!userAutoplayTests) return;

    const stillExistsOnBackend = userAutoplayTests.some(
      (test: any) => test.originalVideoId === videoId
    );

    if (!stillExistsOnBackend) {
      updateABTestConfig({
        enabled: false,
        status: 'finished',
        lastUpdated: new Date().toISOString()
      });
      setShowActivePanel(false);
      reset();
      toast.success(translateInterfaceText("Teste finalizado automaticamente"), {
        description: translateInterfaceText("A data de término foi atingida e o teste foi encerrado.")
      });
    }
  }, [userAutoplayTests, abTestConfig.testId, abTestConfig.status, abTestConfig.endDate, videoId]);

  const updateABTestConfig = (updates: Partial<typeof abTestConfig>) => {
    setFeature('autoplayOverlay', {
      enabled: features.autoplayOverlay?.enabled ?? false,
      ...features.autoplayOverlay,
      abTestConfig: { ...abTestConfig, ...updates }
    });
  };

  const getTrafficPercentage = (variationId: string): number => {
    if (abTestConfig.trafficDistribution?.[variationId]) {
      return abTestConfig.trafficDistribution[variationId];
    }
    return safeVariations.length > 0 ? Math.floor(100 / safeVariations.length) : 0;
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1: return safeVariations.length >= 2;
      case 2: return safeVariations.length >= 2;
      case 3: {
        const total = safeVariations.reduce((sum, v) => sum + getTrafficPercentage(v.id), 0);
        return total === 100;
      }
      default: return false;
    }
  };

  const handleNext = () => {
    if (canGoNext()) {
      setDirection(1);
      completeStep(currentStep);
      nextStep();
    }
  };

  const handleBack = () => {
    setDirection(-1);
    prevStep();
  };

  const handleActivate = async () => {
    if (!videoId) {
      toast.error(translateInterfaceText("Erro ao criar teste"), {
        description: translateInterfaceText("ID do vídeo não encontrado")
      });
      return;
    }

    if (!user?.id) {
      toast.error(translateInterfaceText("Erro ao criar teste"), {
        description: translateInterfaceText("Usuário não autenticado")
      });
      return;
    }

    setIsCreating(true);

    try {
      // Montar configuração com status e datas
      const configWithDates = {
        ...abTestConfig,
        variations: safeVariations,
        status: 'active' as const,
        startDate: new Date().toISOString(),
        endDate: data.endDate ? data.endDate.toISOString() : undefined,
      };

      // Converter para o formato do backend usando o helper
      const payload = autoplayTestService.convertConfigToPayload(
        configWithDates,
        user.id,
        'Teste A/B de Autoplay'
      );

      // Criar teste no backend
      const response = await createTest({
        originalVideoId: videoId,
        payload,
      });

      // Atualizar configuração local com o ID do teste
      updateABTestConfig({
        enabled: true,
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: data.endDate ? data.endDate.toISOString() : undefined,
        lastUpdated: new Date().toISOString(),
        testId: response.id, // Salvar ID do teste retornado pelo backend
      });

      setShowActivePanel(true);
      toast.success(translateInterfaceText("Teste A/B criado e ativado!"), {
        description: data.endDate
          ? translateInterfaceText("Copie o código do teste e atualize o embed no seu site. Término: {value0}", { value0: (format(data.endDate, "Pp", { locale: getInterfaceDateLocale() })) })
          : translateInterfaceText("Copie o código do teste e atualize o embed no seu site para que ele comece a rodar.")
      });
    } catch (error) {
      console.error('Erro ao criar teste:', error);
      // O toast de erro já é mostrado pelo hook useCreateAutoplayTest
    } finally {
      setIsCreating(false);
    }
  };

  const handleFinalize = () => {
    updateABTestConfig({
      enabled: false,
      status: 'finished',
      lastUpdated: new Date().toISOString()
    });
    setShowActivePanel(false);
    reset();
    toast.success(translateInterfaceText("Teste finalizado com sucesso!"));
  };

  const handleExit = () => {
    reset();
  };

  if (!features.autoplayOverlay?.enabled) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertCircle className="mx-auto h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium mb-2">{translateInterfaceText("Autoplay desativado")}</p>
        <p>{translateInterfaceText("Ative o autoplay na aba Configuração para configurar testes A/B")}</p>
      </div>
    );
  }

  // Se o teste está ativo, mostrar painel de teste ativo
  if (showActivePanel && abTestConfig.status === 'active') {
    return (
      <ActiveTestPanel
        testConfig={abTestConfig}
        variations={safeVariations}
        onFinalize={handleFinalize}
        videoId={videoId}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AutoplayABVariationsStep
            variations={safeVariations}
            abTestConfig={abTestConfig}
            onUpdateConfig={updateABTestConfig}
            getTrafficPercentage={getTrafficPercentage}
          />
        );
      case 2:
        return (
          <AutoplayABOrderStep
            variations={safeVariations}
            onUpdateConfig={updateABTestConfig}
          />
        );
      case 3:
        return (
          <AutoplayABTrafficStep
            variations={safeVariations}
            abTestConfig={abTestConfig}
            onUpdateConfig={updateABTestConfig}
            getTrafficPercentage={getTrafficPercentage}
            endDate={data.endDate}
            onEndDateChange={setEndDate}
            durationMode={data.durationMode}
            onDurationModeChange={setDurationMode}
            onActivate={handleActivate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 -mx-6">
      <AutoplayABWizardStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={setCurrentStep}
      />
      
      <div className="flex-1 overflow-y-auto min-h-0 premium-scroll px-6 py-5">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      <AutoplayABWizardFooter
        currentStep={currentStep}
        totalSteps={steps.length}
        canGoNext={canGoNext()}
        onBack={handleBack}
        onNext={handleNext}
        onFinish={handleActivate}
        isFinishing={isCreating}
      />
    </div>
  );
};
